import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Bot, Radio, GraduationCap } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow, EASE } from "@/components/Reveal";
import TickerTape from "@/components/TickerTape";
import Marquee from "@/components/Marquee";
import PricingTeaser from "@/components/PricingTeaser";
import { MarketOverview } from "@/components/TradingView";
import { SERVICES, MANIFESTO, IMAGES } from "@/data/site";

const SERVICE_ICONS = { bots: Bot, signals: Radio, education: GraduationCap };

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "14%"]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

    return (
        <section ref={ref} data-testid="home-hero" className="relative overflow-hidden">
            <div className="pointer-events-none absolute -top-48 right-[-12%] h-[36rem] w-[36rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
            <div className="pointer-events-none absolute bottom-24 left-[-14%] h-[28rem] w-[28rem] rounded-full bg-emerald-500/5 blur-3xl" />

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1.6, ease: EASE, delay: 0.5 }}
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 hidden w-[72%] lg:block"
            >
                <div
                    className="h-full w-full"
                    style={{
                        maskImage: "linear-gradient(to right, transparent 2%, rgba(0,0,0,0.4) 36%, black 68%)",
                        WebkitMaskImage: "linear-gradient(to right, transparent 2%, rgba(0,0,0,0.4) 36%, black 68%)",
                    }}
                >
                    <div
                        className="h-full w-full"
                        style={{
                            maskImage: "linear-gradient(to bottom, transparent 0%, black 24%, black 78%, transparent 98%)",
                            WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 24%, black 78%, transparent 98%)",
                        }}
                    >
                        <motion.img
                            style={{ y: imgY, mixBlendMode: "lighten" }}
                            src={IMAGES.heroGold}
                            alt=""
                            data-testid="hero-image"
                            className="h-full w-full scale-110 object-cover"
                        />
                    </div>
                </div>
            </motion.div>

            <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-end gap-16 px-6 pb-20 pt-40 md:px-10 md:pt-48 lg:grid-cols-12">
                <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>Pocketfolio — Algorithmic trading, refined</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Trade the markets</MaskedLine>
                        <MaskedLine delay={0.34}>that move the</MaskedLine>
                        <MaskedLine delay={0.48}>
                            <span className="font-editorial italic font-light text-gold">world.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.75} className="mt-10 max-w-xl">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Institutional-grade forex bots, high-conviction signals and structured education
                            across commodities, stocks and ETFs — engineered by a registered desk in the
                            Comoros, trusted by traders worldwide.
                        </p>
                    </Reveal>
                    <Reveal delay={0.9} className="mt-12 flex flex-wrap items-center gap-4">
                        <Link
                            to="/services"
                            data-testid="hero-cta-services"
                            className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                        >
                            Explore Services <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/markets"
                            data-testid="hero-cta-markets"
                            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-sm text-white transition-colors duration-300 hover:bg-white hover:text-black"
                        >
                            View Live Markets <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                    <Reveal delay={1.05} className="mt-14">
                        <div className="flex flex-wrap gap-x-10 gap-y-3 font-mono text-xs text-zinc-500">
                            <span data-testid="hero-stat-assets">03 asset classes</span>
                            <span data-testid="hero-stat-desk">24/5 research desk</span>
                            <span data-testid="hero-stat-rules">100% rules-based execution</span>
                        </div>
                    </Reveal>
                </motion.div>

                <div className="relative lg:col-span-5">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.3, ease: EASE, delay: 0.55 }}
                        className="relative lg:hidden"
                    >
                        <div
                            className="overflow-hidden"
                            style={{
                                maskImage: "radial-gradient(ellipse 70% 64% at 50% 50%, black 18%, transparent 72%)",
                                WebkitMaskImage: "radial-gradient(ellipse 70% 64% at 50% 50%, black 18%, transparent 72%)",
                            }}
                        >
                            <motion.img
                                style={{ y: imgY, mixBlendMode: "lighten" }}
                                src={IMAGES.heroGold}
                                alt="Golden candlestick chart — algorithmic trading by Pocketfolio"
                                data-testid="hero-image-mobile"
                                className="h-[460px] w-full scale-[1.25] object-cover"
                            />
                        </div>
                    </motion.div>
                    <Reveal delay={1.1} className="relative z-10 mt-2">
                        <div className="flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                            <span>XAU/USD · WTI · SPX · QQQ</span>
                            <span className="text-gold">Est. Comoros</span>
                        </div>
                    </Reveal>
                </div>
            </div>

            <TickerTape />
        </section>
    );
};

const ServicesBento = () => (
    <section data-testid="home-services" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
        <Reveal>
            <Eyebrow>What we do</Eyebrow>
            <h2 className="mt-6 max-w-2xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                Three instruments.
                <span className="font-editorial italic font-light text-gold"> One standard.</span>
            </h2>
        </Reveal>
        <div className="mt-16 grid gap-6 lg:grid-cols-12">
            {SERVICES.map((s, i) => {
                const Icon = SERVICE_ICONS[s.id];
                return (
                    <Reveal key={s.id} delay={i * 0.12} className={i === 0 ? "lg:col-span-7" : i === 1 ? "lg:col-span-5" : "lg:col-span-12"}>
                        <Link
                            to={`/services#${s.id}`}
                            data-testid={`home-service-${s.id}`}
                            className={`group relative block h-full overflow-hidden border border-white/10 bg-coal/40 ${
                                i === 2 ? "lg:grid lg:grid-cols-2" : ""
                            }`}
                        >
                            <div className={`relative overflow-hidden ${i === 2 ? "h-64 lg:h-full" : "h-56"}`}>
                                <img
                                    src={s.image}
                                    alt={s.name}
                                    className="h-full w-full object-cover opacity-70 transition-transform duration-700 ease-out group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent" />
                            </div>
                            <div className="p-8 md:p-10">
                                <div className="flex items-center gap-3">
                                    <span className="flex h-10 w-10 items-center justify-center border border-gold/30 text-gold">
                                        <Icon size={18} />
                                    </span>
                                    <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                        0{i + 1}
                                    </span>
                                </div>
                                <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight">{s.name}</h3>
                                <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{s.tagline}</p>
                                <span className="mt-8 inline-flex items-center gap-2 text-sm text-gold transition-transform duration-300 group-hover:translate-x-1">
                                    Discover <ArrowUpRight size={15} />
                                </span>
                            </div>
                        </Link>
                    </Reveal>
                );
            })}
        </div>
    </section>
);

const MarketsPreview = () => (
    <section data-testid="home-markets-preview" className="border-t border-white/10 bg-coal/30">
        <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
            <Reveal className="flex flex-wrap items-end justify-between gap-8">
                <div>
                    <Eyebrow>Live markets</Eyebrow>
                    <h2 className="mt-6 max-w-xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                        The tape never sleeps.
                    </h2>
                </div>
                <Link
                    to="/markets"
                    data-testid="markets-preview-link"
                    className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3 text-sm text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                    Open the full terminal <ArrowUpRight size={15} />
                </Link>
            </Reveal>
            <Reveal delay={0.15} className="mt-14">
                <div className="border border-white/10 bg-black/40 p-1">
                    <MarketOverview height={540} testId="home-market-overview" />
                </div>
                <p className="mt-4 font-mono text-[11px] text-zinc-600">
                    Market data provided by TradingView. For information only — not investment advice.
                </p>
            </Reveal>
        </div>
    </section>
);

const Manifesto = () => (
    <section data-testid="home-manifesto" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
        <Reveal>
            <Eyebrow>The Pocketfolio manifesto</Eyebrow>
        </Reveal>
        <div className="mt-10">
            {MANIFESTO.map((m, i) => (
                <Reveal key={m.n} delay={0.05 * i}>
                    <div
                        data-testid={`manifesto-chapter-${m.n}`}
                        className={`grid gap-6 border-t border-white/10 py-14 md:grid-cols-12 md:py-20 ${
                            i % 2 === 1 ? "md:text-right" : ""
                        }`}
                    >
                        <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-3" : ""}`}>
                            <span className="font-editorial italic font-light text-6xl md:text-8xl text-gold/80">
                                {m.n}
                            </span>
                        </div>
                        <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight md:col-span-3">
                            {m.title}
                        </h3>
                        <p className="max-w-xl text-base leading-relaxed text-zinc-400 md:col-span-6 md:justify-self-end">
                            {m.copy}
                        </p>
                    </div>
                </Reveal>
            ))}
        </div>
    </section>
);

const CtaBand = () => (
    <section data-testid="home-cta" className="relative overflow-hidden border-t border-white/10">
        <div className="pointer-events-none absolute inset-x-0 -top-24 mx-auto h-72 w-[42rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
            <Reveal>
                <p className="font-editorial italic font-light text-2xl md:text-3xl text-zinc-400">
                    Ready when you are —
                </p>
                <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
                    Put your capital on a process.
                </h2>
                <Link
                    to="/contact"
                    data-testid="home-cta-contact"
                    className="mt-12 inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                >
                    Start the conversation <ArrowRight size={16} />
                </Link>
            </Reveal>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee />
            <ServicesBento />
            <MarketsPreview />
            <PricingTeaser />
            <Manifesto />
            <CtaBand />
        </>
    );
}
